import {cloneDecoratedData, decorateSnapshot} from '@/api/firebase';
import {bookingEnd, closestBooking, inStartOrder, isActive, isBooked, nextBooking, rootBooking} from '@/utils/bookings';
import {collectValues} from '@/utils/collect';
import {
  addTime,
  currentDate,
  minutes,
  startOfDayUTC,
  toDate,
  toTime
} from '@/utils/dates';
import Defer from '@/utils/defer';
import {valueDiff} from '@/utils/diff';
import {byRef, indexQuerySnapshotUpdates, updateDataToArray} from '@/utils/firestore';
import {scheduleTicks} from '@/utils/schedule';
import {Logger} from '@vanti/vue-logger';
import firebase from 'firebase/app';
import {bookings as impl} from '@/room-booking/impl';
import Vue from 'vue';
import {addDays, endOfDay} from 'date-fns';

const log = Logger.get('bookings');

export default {
  namespaced: true,
  state: {
    byRef: {},
    /**
     * This lookup table records which booking reservation we are interested in.
     * Normally we don't need this information, as we can determine this from the active resource.
     * However, in the case of connected bookings, the active resource is not the same as the reserved resource,
     * which can cause issues for us.
     * We populate this map based on the resource that contributed bookings to the map.
     *
     * @type {Object<string, string>}
     */
    bookingReservationKeys: {},
    /**
     * Represents the most recent 'event' relative to bookings. This is typically a start or end time for known
     * bookings. See changeSchedule for how all the 'ticks' on the timeline are calculated.
     *
     * @type {CanBeDate}
     */
    time: currentDate().toISOString(),
    /**
     * The time around which we fetch bookings for
     *
     * @type {CanBeDate}
     */
    queryTime: currentDate().toISOString()
  },
  getters: {
    now(state) {
      return toDate(state.time);
    },
    bookingByRef(state) {
      return ref => state.byRef[ref];
    },
    reservationKeyForBooking(state) {
      return booking => state.bookingReservationKeys[byRef(booking)];
    },
    knownBookings(state, getters) {
      return inStartOrder(Object.values(state.byRef), b => getters.reservationKeyForBooking(b));
    },
    nonRejectedBookings(state, getters) {
      return getters.knownBookings.filter(b => {
        const approval = impl.approval(b, getters.reservationKeyForBooking(b));
        return approval === undefined || approval.status !== 'REJECTED';
      });
    },
    unconnectedBookings(state, getters) {
      return getters.nonRejectedBookings.filter(b => !b.connection);
    },
    closestBooking(state, getters) {
      return closestBooking(getters.nonRejectedBookings, b => getters.reservationKeyForBooking(b), getters.now);
    },
    nextBooking(state, getters) {
      const last = getters.currentBooking;
      if (!last) return nextBooking(getters.unconnectedBookings, b => getters.reservationKeyForBooking(b), getters.now);
      return nextBooking(getters.unconnectedBookings, b => getters.reservationKeyForBooking(b),
          bookingEnd(last, getters.reservationKeyForBooking(last)));
    },
    /**
     * The current (now) booking is one that intersects the current time, and is checked in
     * @param {Object} state
     * @param {Object} getters
     * @param {Object} rootState
     * @param {Object} rootGetters
     * @return {Booking|null}
     */
    currentBooking(state, getters, rootState, rootGetters) {
      const now = getters.now;
      /** @type {Booking|null} */
      const b = getters.closestBooking;
      if (!b) return null;

      const root = rootBooking(getters.bookingByRef, b);
      if (root && root !== b) {
        if (isActive(b, getters.reservationKeyForBooking(b), now) ||
            isBooked(root, getters.reservationKeyForBooking(root), now) ||
            isActive(root, getters.reservationKeyForBooking(root), now)) {
          return root;
        }
      } else if (isActive(b, getters.reservationKeyForBooking(b), now)) {
        return b;
      }
      return null;
    },
    currentReservationKey(state, getters) {
      return getters.reservationKeyForBooking(getters.currentBooking);
    },
    /**
     * Returns a sorted (ascending) array of all the important times for all known bookings. This is used to
     * calculate when we need to 'wake up' to change our state to correctly represent the active bookings.
     * @param {Object} state
     * @param {Object} getters
     * @param {Object} rootState
     * @param {Object} rootGetters
     * @return {Date[]}
     */
    changeSchedule(state, getters, rootState, rootGetters) {
      // impl notes:
      // 1: Date doesn't play nicely with Set, it appears to be compared based on object identity so we use getTime
      // 2: Numbers aren't sorted correctly in arrays as they are 'converted to strings' before sorting
      const bookings = getters.knownBookings;
      const changes = new Set();
      for (let i = 0; i < bookings.length; i++) {
        const b = bookings[i];
        const reservationKey = getters.reservationKeyForBooking(b);
        // raw times in the booking
        const times = collectValues(b, ...impl.scheduleProps(reservationKey));
        // computed times
        // early check in
        const fromTime = impl.fromTime(b, reservationKey);
        if (fromTime) {
          times.push(addTime(toDate(fromTime), -10 * minutes));
        }
        times.map(v => toTime(v)).forEach(t => changes.add(t));
      }
      return Array.from(changes).sort((a, b) => a - b).map(t => new Date(t));
    },
    currentBookingResourceTitle(state, getters, rootState, rootGetters) {
      const resource = impl.bookingReservedResource(getters.currentBooking, getters.currentReservationKey);
      if (!resource) return null;
      const realResource = rootGetters['resources/resourceByRef'](byRef(resource));
      if (realResource) {
        return realResource.title;
      }
      return resource.title;
    }
  },
  mutations: {
    ...Defer.mutations(log),
    applyBookingsSnapshotUpdate(state, {querySnapshot, resourceRef}) {
      indexQuerySnapshotUpdates(state.byRef, querySnapshot, byRef);
      indexQuerySnapshotUpdates(state.bookingReservationKeys, querySnapshot, byRef, doc => {
        return impl.reservationKey(decorateSnapshot(doc), resourceRef.path);
      });
    },
    forgetBookingsForResourceRef(state, ref) {
      const toRemove = Object.values(state.byRef)
          .filter(b => impl.bookingReservesResource(b, ref));
      toRemove.forEach(b => Vue.delete(state.byRef, byRef(b)));
      toRemove.forEach(b => Vue.delete(state.bookingReservationKeys, byRef(b)));
    },
    clearBookings(state) {
      state.byRef = {};
      state.bookingReservationKeys = {};
    },
    setTime(state, time) {
      state.time = time.toISOString();
    },
    setQueryTime(state, time) {
      state.queryTime = time.toISOString();
    }
  },
  actions: {
    init: {
      root: true,
      handler({rootGetters, dispatch, commit, getters, state}) {
        // schedule time ticks based on bookings
        this.watch(
            () => [getters.changeSchedule],
            () => dispatch('scheduleChanges', currentDate),
            {immediate: true});

        // update the queryPeriod at midnight UTC
        dispatch('scheduleQueryTimeUpdate')
            .catch(err => log.error('During scheduleQueryTimeUpdate', err));

        // fetch bookings when we know which to fetch
        this.watch(
            () => [rootGetters['resources/active/refsContributeBookings'], state.queryTime],
            (newVal, oldVal) => {
              const oldRefs = oldVal && oldVal[0] || [];
              const newRefs = newVal && newVal[0] || [];
              const {added, removed} = valueDiff(oldRefs, newRefs, r => r.path);
              removed.forEach(ref => dispatch('forgetBookingsForResource', ref));
              // if the time has changed, we need to re-watch all the newRefs anyway
              const oldTime = oldVal && oldVal[1] || '';
              const newTime = newVal && newVal[1] || '';
              if (oldTime !== newTime || oldTime === '') {
                newRefs.forEach(ref => dispatch('watchBookingsForResource', ref));
              } else {
                // no change to the period, just watch any added refs
                added.forEach(ref => dispatch('watchBookingsForResource', ref));
              }
            },
            {immediate: true});
      }
    },
    scheduleChanges({getters, commit}, now) {
      commit('defer', {
        schedule: scheduleTicks(getters.changeSchedule, d => commit('setTime', d), now)
      });
    },
    /**
     * Update queryTime at UTC midnight, to refresh the bookings queries.
     *
     * @param {*} context
     * @return {Promise<void>}
     */
    async scheduleQueryTimeUpdate({commit, dispatch, state}) {
      const defer = {};
      const utcMidnight = startOfDayUTC(toDate(state.queryTime));
      const startOfTomorrow = addDays(utcMidnight, 1);
      const msTilStartOfTomorrow = startOfTomorrow.getTime() - Date.now();
      log.debug(`scheduleBookingRefresh in ${msTilStartOfTomorrow}ms`);

      const nextRefreshHandle = setTimeout(() => {
        log.debug('Updating queryTime');
        commit('setQueryTime', currentDate());
        // set up the next refresh
        dispatch('scheduleQueryTimeUpdate')
            .catch(err => log.error('During scheduleQueryTimeUpdate', err));
      }, msTilStartOfTomorrow);
      defer.nextRefreshHandle = () => clearTimeout(nextRefreshHandle);

      commit('defer', defer);
    },
    watchBookingsForResource({state, rootGetters, commit}, resourceRef) {
      log.debug('watchBookingsForResource', resourceRef.path, state.queryTime);
      const defer = impl.watchBookingsForResourceOnDate(resourceRef, state.queryTime,
          querySnapshot => commit('applyBookingsSnapshotUpdate', {querySnapshot, resourceRef}));
      commit('defer', defer);
    },
    forgetBookingsForResource({commit}, resourceRef) {
      commit('reset', [resourceRef.path]);
      commit('forgetBookingsForResourceRef', resourceRef);
    },

    async createAdHocBooking({rootGetters}, mins) {
      const resource = rootGetters['resources/active/snippet'];
      if (!resource) throw new Error('no active resource');
      const createable = rootGetters['createableSnippet'];
      const fromTime = currentDate();
      const toTime = addTime(fromTime, mins * minutes);
      const booking = impl.newAdHocBooking(fromTime, toTime, resource, createable);

      /** @type {firebase.firestore.CollectionReference} */
      const bookings = resource.ref.parent.parent.collection('bookings');
      return bookings.add(booking);
    },
    /**
     * Release or cancel a booking.
     * A function is returned to undo the action
     *
     * @param {Object} [context]
     * @param {DecoratedData} booking
     * @param {boolean} release
     * @return {Promise<function()>}
     */
    async releaseOrCancel({getters}, {booking, release}) {
      if (release) {
        // edit end time to now
        const reservationKey = getters.reservationKeyForBooking(booking);
        const update = {};
        const undo = {};
        update[impl.checkInToProp(reservationKey)] = currentDate();
        undo[impl.checkInToProp(reservationKey)] = firebase.firestore.FieldValue.delete();
        await booking.ref.update(...updateDataToArray(update));

        // return an undo action
        return async () => {
          await booking.ref.update(...updateDataToArray(undo));
        };
      } else {
        const copy = cloneDecoratedData(booking);
        const collection = booking.ref.parent;
        const id = booking.ref.id;
        // remove booking
        await booking.ref.delete();

        // return an undo action
        return async () => {
          await collection.doc(id).set(copy);
        };
      }
    },
    /**
     * Check a booking In.
     * A function is returned to undo the action
     *
     * @param {object} [context]
     * @param {DecoratedData} booking
     * @return {Promise<*>}
     */
    async checkInBooking({getters}, {booking}) {
      const reservationKey = getters.reservationKeyForBooking(booking);
      // add a fromTime to bookings checkIn object
      await booking.ref.update(...updateDataToArray({
        [impl.checkInFromProp(reservationKey)]: firebase.firestore.Timestamp.now()
      }));

      // return the undo action
      return async () => {
        await booking.ref.update(...updateDataToArray({
          [impl.checkInProp(reservationKey)]: firebase.firestore.FieldValue.delete()
        }));
      };
    },
    /**
     * Extend the end time of a booking
     * A function is returned to undo the action
     *
     * @param {object} [context]
     * @param {object} payload
     * @param {DecoratedData} payload.booking
     * @param {number} payload.mins
     * @return {Promise<*>}
     */
    async extendBooking({dispatch, getters}, {booking, mins}) {
      // update reservation reservedPeriod.toTime
      const reservationKey = getters.reservationKeyForBooking(booking);
      const startTime = toDate(impl.toTime(booking, reservationKey));
      let newTime = addTime(startTime, mins * minutes);
      if (startTime.getDate() !== newTime.getDate()) {
        // don't allow extending into tomorrow - bookings aren't in the store to check if it's free
        newTime = endOfDay(startTime);
      }
      const prop = impl.reservedToProp(reservationKey);
      await booking.ref.update({
        [prop]: newTime
      });

      // return the undo action
      return async () => {
        await booking.ref.update({
          [prop]: startTime
        });
      };
    }
  }
};
